import { ChevronDown, ChevronUp, Maximize2, X } from "lucide-react";
import { Suspense } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { DetailLoading } from "~/components/loading";
import {
  Button,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";
import { CategoryDetail } from "./CategoryDetail";

interface CategoryPeekProps {
  /** When set, the sheet is open and shows the detail of this category id. */
  id: string | null;
  onClose: () => void;
  /** Walk to the previous category in the visible list. Pass `null` to hide / disable. */
  onPrevious?: (() => void) | null;
  /** Walk to the next category in the visible list. Pass `null` to hide / disable. */
  onNext?: (() => void) | null;
}

/**
 * Slide-in side panel mirroring V1's `Peek` UX. Triggered by the
 * `selectedCategory` URL search param so a refresh restores the panel state.
 * The maximize icon navigates to the full-page route (V1 parity).
 */
export function CategoryPeek({ id, onClose, onPrevious, onNext }: CategoryPeekProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const open = id !== null;

  const openFullPage = () => {
    if (!id) return;
    onClose();
    navigate(`/admin/categories/${id}`);
  };

  // Hotkeys are bound only while the sheet is open. Empty dep on enabled prevents
  // stale closures since the handlers themselves are read fresh from props.
  useHotkeys("up", () => onPrevious?.(), { enabled: open && !!onPrevious }, [onPrevious, open]);
  useHotkeys("down", () => onNext?.(), { enabled: open && !!onNext }, [onNext, open]);

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent side="right" showClose={false} className="flex flex-col p-0">
        <SheetHeader className="flex flex-row items-center justify-between gap-2">
          <SheetTitle className="capitalize">{t("model.category.modelName")}</SheetTitle>
          <div className="flex items-center gap-1">
            {(onPrevious !== undefined || onNext !== undefined) && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => onPrevious?.()}
                      disabled={!onPrevious}
                      aria-label={t("ui.previous", { defaultValue: "Previous" })}
                    >
                      <ChevronUp />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("ui.previous", { defaultValue: "Previous (↑)" })}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => onNext?.()}
                      disabled={!onNext}
                      aria-label={t("ui.next", { defaultValue: "Next" })}
                    >
                      <ChevronDown />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("ui.next", { defaultValue: "Next (↓)" })}</TooltipContent>
                </Tooltip>
              </>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={openFullPage}
                  aria-label={t("ui.openInFullPage", { defaultValue: "Open full page" })}
                >
                  <Maximize2 />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("ui.openInFullPage", { defaultValue: "Open full page" })}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  aria-label={t("ui.close", { defaultValue: "Close" })}
                >
                  <X />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("ui.close", { defaultValue: "Close" })}</TooltipContent>
            </Tooltip>
          </div>
        </SheetHeader>
        <SheetBody>
          <Suspense fallback={<DetailLoading />}>{id !== null && <CategoryDetail id={id} onBack={onClose} />}</Suspense>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
